import {
  DispatchTrackingDestination,
  DispatchTrackingPerformer,
  DispatchTrackingRoute,
  LocationMetaOrigin,
  toLocationMetaOriginSource
} from "@ots-frontend/ots-data";
import {Box, Divider, Group, Stack, Text, ThemeIcon} from "@mantine/core";
import {IconArrowBackUp, IconMapPin, IconRoute, IconTruckDelivery, IconUser} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {InfoWindow, InfoWindowProps} from "../../../map/info-window/InfoWindow/InfoWindow";
import {InfoWindowBody} from "../../../map/info-window/InfoWindowBody/InfoWindowBody";
import {EntityAnchor} from "../../../anchor/EntityAnchor/EntityAnchor";
import {FormattedDateTime} from "../../../date/FormattedDateTime/FormattedDateTime";
import {DefaultText} from "../../../text/DefaultText/DefaultText";
import {getFormattedQuantity} from "../../../text/FormattedQuantity/FormattedQuantity";
import {getFormattedDatabaseCode} from "../../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type DispatchTrackingInfoWindowProps = Omit<InfoWindowProps, "children"> & {
  kind: "performer" | "destination" | "source";
  performer?: DispatchTrackingPerformer;
  destination?: DispatchTrackingDestination;
  origin?: LocationMetaOrigin;
  route?: DispatchTrackingRoute;
}

const getRouteSummary = (route?: DispatchTrackingRoute): string | undefined => {
  if (!route) {
    return undefined;
  }

  const values: string[] = [];
  if (typeof route.distanceMiles === "number") {
    values.push(`${route.distanceMiles.toFixed(1)} mi`);
  }
  if (typeof route.durationMillis === "number") {
    values.push(`${Math.round(route.durationMillis / 60000)} min`);
  }

  return values.length > 0 ? values.join(" / ") : route.status;
}

export const DispatchTrackingInfoWindow = (props: DispatchTrackingInfoWindowProps) => {
  const {kind, performer, destination, origin, route, options, ...rest} = props;
  const {t} = useTranslation(["common"]);

  const location = performer?.locationContext?.location;
  const isPerformer = kind === "performer";
  const isSource = kind === "source";

  return (
    <InfoWindow
      {...rest}
      options={{
        ...options,
        pixelOffset: options?.pixelOffset ?? new google.maps.Size(0, -30)
      }}>
      <InfoWindowBody>
        <Box style={{maxWidth: 280}}>
          <Stack spacing={6}>
            <Group spacing="xs" noWrap>
              <ThemeIcon variant="light" c={isPerformer ? "green" : isSource ? "yellow" : "red"} radius="xl" size="sm">
                {isPerformer ? <IconTruckDelivery size={14}/> : isSource ? <IconArrowBackUp size={14}/> : <IconMapPin size={14}/>}
              </ThemeIcon>
              <Text fz="sm" fw={500} lineClamp={2}>
                {isPerformer ? (
                  performer?.personId ? (
                    <EntityAnchor
                      value={{
                        objectId: performer.personId,
                        objectName: performer.personName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: "PERSON"
                      }}/>
                  ) : (
                    <DefaultText value={performer?.personName}/>
                  )
                ) : isSource ? (
                  origin?.siteId ? (
                    <EntityAnchor
                      value={{
                        objectId: origin.siteId,
                        objectName: origin.siteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: "SITE"
                      }}/>
                  ) : (
                    <DefaultText value={origin?.siteName}/>
                  )
                ) : (
                  destination?.siteId ? (
                    <EntityAnchor
                      value={{
                        objectId: destination.siteId,
                        objectName: destination.siteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: "SITE"
                      }}/>
                  ) : (
                    <DefaultText value={destination?.siteName}/>
                  )
                )}
              </Text>
            </Group>

            <Divider my={4} variant="dashed"/>

            {isPerformer ? (
              <Stack spacing={3}>
                {performer?.personCompanyName &&
                  <Group spacing="xs" noWrap>
                    <IconUser size={14}/>
                    <Text fz="xs" c="dimmed">{performer.personCompanyName}</Text>
                  </Group>
                }
                <Text fz="xs" c="dimmed">
                  {t("common.entity.dispatch.tracking.infowindow.lastlocation", "Last Location")}:{" "}
                  <FormattedDateTime date={performer?.locationDttm ?? location?.dttm}/>
                </Text>
                {location &&
                  <Text fz="xs" c="dimmed">
                    {getFormattedQuantity(location.latitude, undefined, {maximumFractionDigits: 5})},{" "}
                    {getFormattedQuantity(location.longitude, undefined, {maximumFractionDigits: 5})}
                  </Text>
                }
                {typeof location?.speed === "number" &&
                  <Text fz="xs" c="dimmed">
                    {t("common.entity.dispatch.tracking.infowindow.speed", "Speed")}: {getFormattedQuantity(location.speed, "mph")}
                  </Text>
                }
              </Stack>
            ) : isSource ? (
              <Stack spacing={3}>
                <Text fz="xs" c="dimmed">
                  {t("common.entity.dispatch.tracking.field.source", "Coming From")}
                </Text>
                {origin?.siteCompanyName &&
                  <Text fz="xs" c="dimmed">{origin.siteCompanyName}</Text>
                }
                {origin?.source &&
                  <Text fz="xs" c="dimmed">{getFormattedDatabaseCode(toLocationMetaOriginSource(origin.source), t)}</Text>
                }
                {origin?.transportDttm &&
                  <Text fz="xs" c="dimmed">
                    {t("common.entity.dispatch.tracking.infowindow.transporttime", "Transport")}:{" "}
                    <FormattedDateTime date={origin.transportDttm}/>
                  </Text>
                }
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Text fz="xs" c="dimmed">
                  {destination?.status === "ARRIVED"
                    ? t("common.entity.dispatch.tracking.destination.arrived.short", "Arrived")
                    : destination?.type ? `${destination.type.charAt(0)}${destination.type.slice(1).toLowerCase()}` : t("common.entity.dispatch.tracking.field.destination", "Destination")}
                </Text>
                {destination?.siteCompanyName &&
                  <Text fz="xs" c="dimmed">{destination.siteCompanyName}</Text>
                }
              </Stack>
            )}

            {(route || route?.etaDttm) &&
              <>
                <Divider my={4} variant="dashed"/>
                <Group spacing="xs" noWrap>
                  <IconRoute size={14}/>
                  <Text fz="xs" c="dimmed">
                    <DefaultText value={getRouteSummary(route)}/>
                  </Text>
                </Group>
                {route?.etaDttm &&
                  <Text fz="xs" c="dimmed">
                    {t("common.entity.dispatch.tracking.eta", "ETA")} <FormattedDateTime date={route.etaDttm}/>
                  </Text>
                }
              </>
            }
          </Stack>
        </Box>
      </InfoWindowBody>
    </InfoWindow>
  );
}
